import React, { useContext, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, Typography } from '@mui/material';
import { AuthContext } from './context/AuthContext';

// Layout Components
import Layout from './components/layout/Layout';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import DoctorRoute from './components/auth/DoctorRoute';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const DoctorsPage = lazy(() => import('./pages/DoctorsPage'));
const DoctorDetailPage = lazy(() => import('./pages/DoctorDetailPage'));
const BookAppointmentPage = lazy(() => import('./pages/patient/BookAppointmentPage'));
const PatientDashboardPage = lazy(() => import('./pages/patient/DashboardPage'));
const PatientAppointmentsPage = lazy(() => import('./pages/patient/AppointmentsPage'));
const PatientProfilePage = lazy(() => import('./pages/patient/ProfilePage'));
const DoctorDashboardPage = lazy(() => import('./pages/doctor/DashboardPage'));
const DoctorAppointmentsPage = lazy(() => import('./pages/doctor/AppointmentsPage'));
const DoctorSchedulePage = lazy(() => import('./pages/doctor/SchedulePage'));
const DoctorPatientsPage = lazy(() => import('./pages/doctor/PatientsPage'));
const DoctorProfilePage = lazy(() => import('./pages/doctor/ProfilePage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const AdminDoctorsPage = lazy(() => import('./pages/admin/DoctorsPage'));
// Update these imports to reference existing files or components we'll create
// Instead of importing non-existent files, we'll define them inline
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Create inline admin pages for the missing components
const AdminPatientsPage = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" component="h1" gutterBottom>
      Admin Patients Management
    </Typography>
    <Typography variant="body1">
      This page is under construction. Patient management features will be available soon.
    </Typography>
  </Box>
);

const AdminAppointmentsPage = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" component="h1" gutterBottom>
      Admin Appointments Management
    </Typography>
    <Typography variant="body1">
      This page is under construction. Appointment management features will be available soon.
    </Typography>
  </Box>
);

const App = () => {
  const { isAuthenticated, user, loading } = useContext(AuthContext);

  // Loading state while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CssBaseline />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="doctors" element={<DoctorsPage />} />
            <Route path="doctors/:id" element={<DoctorDetailPage />} />

            {/* Auth Routes */}
            <Route
              path="login"
              element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="register"
              element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" replace />}
            />

            {/* Protected Routes */}
            <Route
              path="dashboard"
              element={
                <PrivateRoute>
                  {user?.role === 'admin' ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : user?.role === 'doctor' ? (
                    <Navigate to="/doctor/dashboard" replace />
                  ) : (
                    <PatientDashboardPage />
                  )}
                </PrivateRoute>
              }
            />

            {/* Patient Routes */}
            <Route
              path="appointments"
              element={
                <PrivateRoute>
                  <PatientAppointmentsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="book-appointment/:doctorId"
              element={
                <PrivateRoute>
                  <BookAppointmentPage />
                </PrivateRoute>
              }
            />
            <Route
              path="profile"
              element={
                <PrivateRoute>
                  <PatientProfilePage />
                </PrivateRoute>
              }
            />

            {/* Doctor Routes */}
            <Route
              path="doctor/dashboard"
              element={
                <DoctorRoute>
                  <DoctorDashboardPage />
                </DoctorRoute>
              }
            />
            <Route
              path="doctor/appointments"
              element={
                <DoctorRoute>
                  <DoctorAppointmentsPage />
                </DoctorRoute>
              }
            />
            <Route
              path="doctor/schedule"
              element={
                <DoctorRoute>
                  <DoctorSchedulePage />
                </DoctorRoute>
              }
            />
            <Route
              path="doctor/patients"
              element={
                <DoctorRoute>
                  <DoctorPatientsPage />
                </DoctorRoute>
              }
            />
            <Route
              path="doctor/profile"
              element={
                <DoctorRoute>
                  <DoctorProfilePage />
                </DoctorRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/doctors"
              element={
                <AdminRoute>
                  <AdminDoctorsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/patients"
              element={
                <AdminRoute>
                  <AdminPatientsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/appointments"
              element={
                <AdminRoute>
                  <AdminAppointmentsPage />
                </AdminRoute>
              }
            />

            {/* Not Found Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
};

export default App;